/** 새 서비스워커 활성화를 기다리는 상한. 넘기면 기다림을 포기하고 그대로 진행한다. */
export const SERVICE_WORKER_UPDATE_TIMEOUT_MS = 8_000;

/**
 * 서비스워커 갱신을 요청하고, 새 워커가 설치돼 활성화될 때까지 상한 안에서 기다린다.
 *
 * 「새 버전」 안내를 띄우기 전에 부른다. 안내 뒤 첫 일반 새로고침이 옛 프리캐시 셸(옛 번들)을
 * 받지 않으려면, 그 전에 새 워커가 이미 활성화돼 있어야 한다(`sw.ts` 가 skipWaiting·claim 한다).
 * 갱신 요청은 브라우저의 자동 확인(탐색·24시간 주기)과 별개로 즉시 새 워커를 찾게 한다.
 * 거부하지 않는다. 반환값은 「지금 활성화된 워커가 최신인가」다 — 새 워커가 없거나 활성화됐으면
 * true, 설치가 상한을 넘기거나 실패하면 false(서비스워커 미지원은 옛 셸을 내줄 워커가 없으니 true). 호출부는 false 일 때 일반 새로고침이 옛
 * 번들을 줄 수 있음을 알고 안내를 미뤄야 한다. 설치는 백그라운드에서 계속된다.
 */
export function updateServiceWorker(
  timeoutMs: number = SERVICE_WORKER_UPDATE_TIMEOUT_MS
): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    const settle = (ok: boolean) => {
      clearTimeout(timer);
      resolve(ok);
    };

    void (async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      // 등록이 없으면 프리캐시 셸을 내줄 워커도 없으니 새로고침이 곧 최신이다.
      if (!registration) return true;
      await registration.update();
      const worker = registration.installing ?? registration.waiting;
      if (!worker || worker.state === 'activated') return true;
      return new Promise<boolean>((done) => {
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') done(true);
          else if (worker.state === 'redundant') done(false);
        });
      });
    })().then(settle, () => settle(false));
  });
}
